// watch/UpNextRowMenu.tsx — the ⋮ on each Up next row (concept video_watch_page_v2, 1.27.60):
// Play next, Add to end of queue, Save to Watch later. Sits beside the row's link, never
// inside it, so opening the menu doesn't navigate.
import { useState } from 'react';
import { IconButton, Popover, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useRequireAuth } from '@/hooks';
import { videoFeedService } from '@/core/services/videoFeedService';
import type { Post } from '@/core/models/post';
import { useVideoQueue } from '../../queue/videoQueue';
import { MenuItem, MenuPanel } from './actionStyles';

export function UpNextRowMenu({ post, title }: { post: Post; title: string }) {
  const [open, setOpen] = useState(false);
  const toast = useToast();
  const requireAuth = useRequireAuth();
  const playNext = useVideoQueue((s) => s.playNext);
  const addToEnd = useVideoQueue((s) => s.addToEnd);

  function run(action: () => void) {
    setOpen(false);
    action();
  }

  return (
    <Popover
      trigger={<IconButton icon="more-vertical" size={18} label={`More actions for ${title}`} />}
      open={open}
      onOpenChange={setOpen}
      align="end"
    >
      <MenuPanel role="menu" aria-label={`More actions for ${title}`}>
        <MenuItem
          type="button"
          role="menuitem"
          onClick={() => run(() => (playNext(post), toast('Plays next')))}
        >
          <Icon name="list-plus" size={16} />
          <span>Play next</span>
        </MenuItem>
        <MenuItem
          type="button"
          role="menuitem"
          onClick={() => run(() => (addToEnd(post), toast('Added to the end of your queue')))}
        >
          <Icon name="list" size={16} />
          <span>Add to end of queue</span>
        </MenuItem>
        <MenuItem
          type="button"
          role="menuitem"
          onClick={() =>
            run(() => {
              if (!requireAuth('save videos')) return;
              void videoFeedService
                .saveForLater(post.id)
                .then(() => toast('Saved to Watch later'))
                .catch(() => toast('Something went wrong'));
            })
          }
        >
          <Icon name="clock" size={16} />
          <span>Save to Watch later</span>
        </MenuItem>
      </MenuPanel>
    </Popover>
  );
}
